import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogContent,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { issuesRef } from '../../api/firebase/firestore/issue';
import {
  mediaInquiry,
  negotiationsRef
} from '../../api/firebase/firestore/negotiation';
import { inquirySchema } from '../../api/firebase/firestore/negotiation/validation';
import { getUserInfo } from '../../api/firebase/firestore/user';
import {
  black,
  blackText,
  blue,
  gray,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import { buttonFont, textMb } from '../../constants/Location';
import { screenType, status } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';

import CustomSelectField from './CustomSelectField';
import CustomTextfield from './CustomTextfield';

interface ContactType {
  issue: string;
  category: string;
  message: string;
}

const styles = {
  buttonFont,
  buttonWrapper: {
    columnGap: '15px',
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  closeButton: {
    ':hover': {
      bgcolor: 'transparent',
      boxShadow: 'none'
    },
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '96px'
    },
    bgcolor: 'white',
    border: `1px solid ${gray}`,
    boxShadow: 'none',
    height: '42px',
    width: '124px'
  },
  closeIcon: {
    color: 'white',
    cursor: 'pointer'
  },
  confirmButton: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '199px'
    },
    height: '42px',
    width: '260px'
  },
  drawer: {
    '& .MuiBackdrop-root': {
      bgcolor: 'transparent'
    },
    '& .MuiDrawer-paper': {
      border: 'none',
      borderRadius: '4px',
      bottom: '16px',
      left: 'unset',
      right: '32px',
      width: '300px'
    },
    '@media screen and (max-width:480px)': {
      '& .MuiDrawer-paper': {
        borderRadius: '0',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%'
      }
    }
  },
  header: {
    bgcolor: black,
    height: '48px',
    padding: '12px 16px'
  },
  headerText: {
    color: 'white'
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginLeft: '24px'
  },
  label: {
    fontSize: '12px'
  },
  labelFont: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    fontSize: '12px',
    marginLeft: '24px'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalCheckIcon: {
    color: blue,
    height: '56px',
    margin: '0 auto',
    width: '56px'
  },
  modalClose: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  modalDesc: {
    fontSize: '22px'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  }
};

interface Props {
  screen?: number;
  mediaId: string;
  toggleDrawer: boolean;
  handleDrawerToggle: () => void;
  onClose: (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
}

const ContactDrawer: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const { screen, mediaId, toggleDrawer, handleDrawerToggle, onClose } = props;
  const navigate = useNavigate();
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [negotiationId, setNegotiationId] = useState('');

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactType>({
    resolver: yupResolver(inquirySchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('issue') === undefined ||
        0 ||
        watch('category') === undefined ||
        watch('message') === undefined ||
        '' ||
        Boolean(errors.issue) ||
        Boolean(errors.category) ||
        Boolean(errors.message)
      );
    }
    return false;
  };

  const [modalOpen, setModalOpen] = React.useState(false);
  const modalClose = (): void => {
    setModalOpen(false);
  };

  const onSubmit = async (): Promise<void> => {
    if (currentUser) {
      let clientId = '';
      if (screen === screenType.agent) {
        const issueId = watch('issue');
        const issueRef = doc(issuesRef, issueId);
        const snapShot = await getDoc(issueRef);
        const issueData = snapShot.data();
        clientId = issueData?.companyId as string;
      }
      const negId = await mediaInquiry(
        screen ?? 0,
        currentUser.uid,
        watch,
        mediaId,
        clientId
      );
      setSubmitted(true);
      setNegotiationId(negId);
      setModalOpen(true);
      setSubmitted(false);
    }
  };

  const [issueList, setIssueList] = useState<
    Array<{ value: string; option: string }>
  >([]);

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    void (async () => {
      // 問い合わせ用与件リストの作成
      if (currentUser) {
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        let q;
        if (screen === screenType.client) {
          q = query(
            issuesRef,
            where('companyId', '==', comId),
            where('status', '==', status.approved)
          );
        } else {
          q = query(
            issuesRef,
            where('agentId', '==', comId),
            where('status', '==', status.approved)
          );
        }
        const issueSnap = await getDocs(q);
        const list: Array<{
          disabled: boolean;
          value: string;
          option: string;
        }> = [];

        // すでに商談を進めているissueは選択不可にする
        const matchedIssueIds: string[] = [];
        const negotiationQuery = query(
          negotiationsRef,
          where('mediaId', '==', mediaId)
        );
        const negotiationSnap = await getDocs(negotiationQuery);

        negotiationSnap.forEach((document) => {
          matchedIssueIds.push(document.data()?.issueId as string);
        });

        issueSnap.forEach((docs) => {
          const issueId = docs.data().issueId as string;
          const isMatched = matchedIssueIds.includes(issueId);
          const issueData = {
            disabled: isMatched,
            option: String(docs.data().name),
            value: issueId
          };
          list.push(issueData);
        });
        setIssueList(list);
      }
    })();
  }, [mediaId, submitted]);

  return (
    <>
      <Drawer
        open={toggleDrawer}
        anchor={'bottom'}
        disableScrollLock={true}
        sx={styles.drawer}
      >
        <Grid container flexDirection={'column'}>
          <Grid item sx={{ width: '100%' }}>
            <Grid
              sx={styles.header}
              container
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography component="p" sx={styles.headerText}>
                {t('media.mediaContact')}
              </Typography>
              <CloseIcon
                sx={styles.closeIcon}
                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
                  onClose(e);
                  reset();
                }}
              />
            </Grid>
          </Grid>
          <Grid item sx={{ padding: '24px 16px', width: '100%' }}>
            <Controller
              render={({ field }) => (
                <CustomSelectField
                  {...field}
                  labelText={t('contact.issue')}
                  placeholder={t('form.empty')}
                  isRequired
                  requiredText={t('form.required')}
                  error={Boolean(errors.issue)}
                  helperText={errors.issue?.message}
                  defaultValue={0}
                  list={issueList ?? []}
                  width="contactDrawer"
                  blank
                />
              )}
              control={control}
              name="issue"
            />
            <Controller
              render={({ field }) => (
                <Typography sx={textMb} align="center" component="div">
                  <Typography align="left" component="div">
                    <Typography color={gray} sx={styles.label} component="span">
                      {t('contact.category')}
                    </Typography>
                    <Typography component="span" sx={styles.required}>
                      {t('form.required')}
                    </Typography>
                  </Typography>
                  <Typography align="left" component="div">
                    <FormControl>
                      <RadioGroup
                        {...field}
                        aria-labelledby="demo-form-control-label-placement"
                        name="position"
                        sx={{
                          '&.MuiFormGroup-root': {
                            alignContent: 'flex-start'
                          }
                        }}
                      >
                        <>
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('inquiry.category.1')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('inquiry.category.2')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={3}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('inquiry.category.3')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={4}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('inquiry.category.4')}
                            sx={blackText}
                          />
                        </>
                      </RadioGroup>
                    </FormControl>
                  </Typography>
                  <FormHelperText
                    sx={styles.helper}
                    error={Boolean(errors.category)}
                  >
                    {errors.category?.message}
                  </FormHelperText>
                </Typography>
              )}
              control={control}
              name="category"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                  isRequired
                  requiredText={t('form.required')}
                  labelText={t('contact.message')}
                  multiline
                  rows={4}
                  fieldType="textarea"
                  width="contactDrawer"
                />
              )}
              control={control}
              name="message"
            />
            <Button
              disabled={buttonDisabledFlag()}
              variant="contained"
              sx={[
                primaryButtonBackground,
                {
                  '@media screen and (max-width:960px)': {
                    height: '42px'
                  },
                  width: '100%'
                }
              ]}
              onClick={async (
                e: React.MouseEvent<HTMLButtonElement, MouseEvent>
              ) => {
                e.stopPropagation();
                setFirstSubmit(true);
                await handleSubmit(onSubmit)();
              }}
            >
              <Typography component="div" color="white" sx={{ fontSize: 15 }}>
                {t('contact.send')}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Drawer>
      <Dialog onClose={modalClose} open={modalOpen} sx={styles.modalWrapper}>
        <DialogContent sx={styles.modal}>
          <Typography component="div" sx={styles.modalClose}>
            <CloseIcon
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(false);
                handleDrawerToggle();
                setFirstSubmit(false);
                reset();
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Typography>
          <Grid container justifyContent={'center'}>
            <CheckCircleIcon sx={styles.modalCheckIcon} />
          </Grid>
          <Grid container justifyContent="center" sx={{ margin: '24px 0' }}>
            <Typography component="p" sx={styles.modalDesc}>
              {t('inquiry.complete')}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            alignItems="center"
            sx={styles.buttonWrapper}
          >
            <Button
              variant="contained"
              sx={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(false);
                handleDrawerToggle();
                setFirstSubmit(false);
                reset();
              }}
            >
              <Typography
                component="div"
                color={blackText}
                sx={styles.buttonFont}
              >
                {t('inquiry.close')}
              </Typography>
            </Button>
            <Button
              variant="contained"
              sx={[primaryButtonBackground, styles.confirmButton]}
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.stopPropagation();
                if (screen === screenType.client) {
                  navigate(`/client/mypage/message/${negotiationId}`);
                } else if (screen === screenType.agent) {
                  navigate(`/agent/mypage/message/${negotiationId}`);
                }
              }}
            >
              <Typography component="div" color="white" sx={styles.buttonFont}>
                {t('inquiry.confirm')}
              </Typography>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactDrawer;
